/**
 * OffsetController — the Offset transform-handle machinery (issue
 * #29 / #244 / #681) extracted from ConstructionTool (#837, epic
 * #828). A tool OWNS a controller instance and DRIVES it by
 * composition (it is NOT a base class), mirroring the
 * `RotationController` / `DeleteSliceController` pattern: the
 * controller owns the offset drag STATE (`offsetDrag`) plus the
 * offset sub-mode's peripheral preset / live-readout fields, while
 * everything construction-specific it needs from the owning tool
 * (scope / scene / project / commit / selection, the detached
 * "simple offset" flag) is injected through the `OffsetHost`
 * interface.
 *
 * The heavy geometry lives in `src/model/constructionOffset.ts`
 * (`computeHeuristicOffset` / `computeOffsetPathData` /
 * `computeSingleSegmentOffsetPath`); only the hover / preview /
 * commit state + handlers move here.
 *
 * ConstructionTool's `onDown` (offset sub-mode + the Edit-mode
 * offset-distance-edit redirect) seeds a drag through `beginCreate`
 * / `beginEdit`; its `onMove` / `onUp` / `onCancel` delegate the
 * offset case, preserving the exact original dispatch order.
 */
import paper from "paper";
import type { Scene, SceneSelection } from "../canvas/Scene";
import {
  projectConstructionLines,
  nextConstructionOffsetName,
  type ConstructionLine,
  type Project,
} from "../model/Project";
import { withConstructionLine } from "../model/projectOps";
import {
  computeHeuristicOffset,
  computeOffsetPathData,
  computeSingleSegmentOffsetPath,
} from "../model/constructionOffset";
import { newId } from "../model/ids";
import { cssVar } from "../colors";

/** Dashed stroke colour for the live offset preview. */
const PREVIEW_STROKE = () => cssVar("--construction-preview");

/** Minimum |distance| an offset drag must reach before release
 *  commits — a click without a meaningful drag is a no-op. */
const OFFSET_MIN_DISTANCE_MM = 0.5;

/** Everything the OffsetController needs from its owning tool. The
 *  offset drag-state field lives in the controller; the hit-testing
 *  / source resolution that seeds it stays in ConstructionTool (it's
 *  shared with the tool's onDown dispatch) and the resolved ids are
 *  handed in through `beginCreate` / `beginEdit`. */
export interface OffsetHost {
  getScope(): paper.PaperScope;
  getScene(): Scene;
  getProject(): Project;
  commit(label: string, mutate: (proj: Project) => Project): void;
  setSelection(sel: SceneSelection): void;
  /** True in the detached "simple offset" sub-mode (issue #344) —
   *  the commit produces a standalone line with no `offsetSource`. */
  isSimpleOffset(): boolean;
}

export class OffsetController {
  /** Offset sub-mode drag state (issue #29). Engaged on `onDown`
   *  when the user clicks an existing construction line while the
   *  Offset sub-mode is active. Per-frame `onMove` recomputes the
   *  signed perpendicular distance from the cursor to the source
   *  path and previews the result; `onUp` commits a derived
   *  ConstructionLine carrying an `offsetSource` ref. */
  private offsetDrag: {
    sourceId: string;
    /** Pointer position at down — used to detect "click without
     *  drag" gestures that should be ignored. */
    start: { x: number; y: number };
    /** Signed distance in project mm of the cursor's most recent
     *  position from the source path. Sign convention: outside a
     *  closed shape (or the user's chosen side of an open path) is
     *  positive; inside / opposite side is negative. */
    distance: number;
    /** Ephemeral preview path drawn at the offset distance. Removed
     *  on every per-frame update and on commit / cancel. */
    previewItem: paper.Item | null;
    /** Set when the drag is editing an existing offset line rather
     *  than creating a new one — the id of the derived line whose
     *  `offsetSource.distance` we'll rewrite on release. When unset
     *  the drag commits a NEW offset line attached to `sourceId`. */
    editingId?: string;
    /** Single-segment offset (issue #681): when set, the drag offsets
     *  just this ONE edge of the source (the curve at `curveIndex` of
     *  subpath `pathIndex`) instead of the whole outline. */
    segment?: { pathIndex: number; curveIndex: number };
  } | null = null;

  /** User-typed offset distance (#29). When non-null the Offset
   *  sub-mode commits with this magnitude on release regardless of
   *  drag distance; the cursor's side relative to the source line
   *  picks the sign. `null` falls back to the legacy drag-defined
   *  distance. */
  private presetOffsetDistance: number | null = null;
  /** Fired during an offset drag so the properties panel can mirror
   *  the live perpendicular distance in its input. Inert when the
   *  user has typed a preset value (`presetOffsetDistance !== null`). */
  private liveOffsetDistanceCallback?: (distance: number) => void;
  /** Rounded-corner preference for offsets this tool is about to make
   *  (issue #423). Mirrors `offsetSource.roundedCorners`, whose default
   *  is ON — an outward offset of a sharp corner is an arc of the
   *  offset distance, which is what "expand uniformly" means. Feeds the
   *  preview AND the commit from this one field so the dashed outline
   *  can't promise corners the committed line won't have. */
  private roundedCorners = true;

  constructor(private host: OffsetHost) {}

  setPresetOffsetDistance(mm: number | null): void {
    this.presetOffsetDistance = mm;
  }

  setRoundedCorners(on: boolean): void {
    this.roundedCorners = on;
  }

  setLiveOffsetDistanceCallback(
    fn: ((distance: number) => void) | undefined,
  ): void {
    this.liveOffsetDistanceCallback = fn;
  }

  // -------------------------------------------------------------------
  // Pointer-flow delegation (called by ConstructionTool)
  // -------------------------------------------------------------------

  /** Seed a NEW offset drag from the Offset sub-mode's onDown. The
   *  source id (and optional single-edge selector) are resolved by
   *  the tool's hit-testing; we just capture the drag + preview. */
  beginCreate(
    sourceId: string,
    p: { x: number; y: number },
    segment?: { pathIndex: number; curveIndex: number },
  ): void {
    this.cancel();
    this.offsetDrag = {
      sourceId,
      start: { x: p.x, y: p.y },
      distance: 0,
      previewItem: null,
      ...(segment ? { segment } : {}),
    };
    this.refreshOffsetPreview(p);
  }

  /** Seed an offset-distance EDIT drag (issue #244 follow-up) — the
   *  Edit-mode redirect when the user grabs the stroke of an existing
   *  offset line. The tool resolves the offset line + its source and
   *  hands the current distance in; on release we rewrite
   *  `offsetSource.distance` instead of creating a new line. */
  beginEdit(
    sourceId: string,
    editingId: string,
    distance: number,
    p: { x: number; y: number },
  ): void {
    this.cancel();
    this.offsetDrag = {
      sourceId,
      start: { x: p.x, y: p.y },
      distance,
      previewItem: null,
      editingId,
    };
    this.liveOffsetDistanceCallback?.(distance);
    this.refreshOffsetPreview(p);
  }

  /** onMove delegation: recompute the offset preview. Returns true
   *  when an offset drag consumed the move. */
  handleMove(p: { x: number; y: number }): boolean {
    if (!this.offsetDrag) return false;
    // Per-frame: recompute signed perpendicular distance and update
    // the dashed preview path so the user sees exactly where the
    // offset will land if they release now.
    this.refreshOffsetPreview(p);
    return true;
  }

  /** onUp delegation: commit the offset (create or distance-edit)
   *  and clear the drag. Returns true when an offset drag consumed
   *  the release. */
  handleUp(): boolean {
    if (!this.offsetDrag) return false;
    const drag = this.offsetDrag;
    // Drop the preview *before* the commit so it doesn't briefly
    // overlap the freshly-rendered derived line.
    if (drag.previewItem) drag.previewItem.remove();
    this.offsetDrag = null;
    if (Math.abs(drag.distance) < OFFSET_MIN_DISTANCE_MM) return true;
    if (drag.editingId) {
      this.commitOffsetDistanceEdit(drag.editingId, drag.distance);
    } else {
      this.commitOffsetLine(drag.sourceId, drag.distance, drag.segment);
    }
    return true;
  }

  /** Drop any in-flight offset drag and remove its preview. Called
   *  from cancelDraft / onCancel / sub-mode switches so the user
   *  doesn't see a stale dashed outline after they back out. */
  cancel(): void {
    if (!this.offsetDrag) return;
    if (this.offsetDrag.previewItem) this.offsetDrag.previewItem.remove();
    this.offsetDrag = null;
    this.host.getScene().clearGuideRays();
  }

  // -------------------------------------------------------------------
  // Commit
  // -------------------------------------------------------------------

  /** Commit a new derived offset construction line. For primitive
   *  sources (issue #244 — circle / ellipse so far), the offset is
   *  built directly from the primitive's geometry and the new line
   *  picks up a matching primitive + the source's rotation so it can
   *  participate in center-snap / primitive resize. Falls back to
   *  the paperjs-offset path for non-primitive sources. */
  private commitOffsetLine(
    sourceId: string,
    distance: number,
    segment?: { pathIndex: number; curveIndex: number },
  ) {
    const project = this.host.getProject();
    const source = projectConstructionLines(project).find(
      (l) => l.id === sourceId,
    );
    if (!source) return;
    const scope = this.host.getScope();
    // Single-segment offset (issue #681): a straight line parallel to
    // one edge, with no heuristic primitive/rounding metadata. Whole-
    // shape offset keeps its heuristic/paperjs-offset path.
    const heuristic = segment
      ? null
      : computeHeuristicOffset(source, distance, scope, {
          roundedCorners: this.roundedCorners,
        });
    const derivedPath = segment
      ? computeSingleSegmentOffsetPath(source, distance, segment, scope)
      : heuristic
        ? heuristic.pathData
        : computeOffsetPathData(source.pathData, distance, scope);
    if (!derivedPath) return;
    const detached = this.host.isSimpleOffset();
    const name = nextConstructionOffsetName(project, source);
    const newLine: ConstructionLine = {
      id: newId("con"),
      name,
      pathData: derivedPath,
      // Simple offset (issue #344): commit the computed geometry with
      // NO `offsetSource`, so the line stands alone — the derive pass
      // never touches it. The live "modifier" offset keeps the link.
      // A single-segment offset carries its edge selector so the
      // deriver re-follows that one edge (issue #681).
      ...(detached
        ? {}
        : {
            offsetSource: {
              sourceId,
              distance,
              ...(segment ? { segment } : {}),
              // Only stored when the user opted OUT — `undefined` and
              // `true` mean the same thing everywhere this is read, so
              // writing `true` would just be noise in the project file
              // (issue #423).
              ...(this.roundedCorners ? {} : { roundedCorners: false }),
            },
          }),
    };
    if (heuristic) {
      if (heuristic.primitive) {
        newLine.primitive = heuristic.primitive;
      }
      if (heuristic.kind) {
        newLine.kind = heuristic.kind;
      }
      if (heuristic.cornerRounding) {
        newLine.cornerRounding = heuristic.cornerRounding;
      }
      if (source.rotation !== undefined && Math.abs(source.rotation) > 1e-6) {
        newLine.rotation = source.rotation;
      }
      if (source.rotationCenter !== undefined) {
        newLine.rotationCenter = source.rotationCenter;
      }
    }
    this.host.commit("Add offset construction line", (proj) =>
      withConstructionLine(proj, newLine),
    );
    this.host.setSelection({
      pieceId: null,
      decorationId: null,
      constructionId: newLine.id,
    });
  }

  /** Rewrite an existing offset line's `offsetSource.distance` from
   *  the Offset sub-tool's edit drag (issue #244 follow-up). The
   *  `deriveConstructionOffsets` pass in the commit pipeline picks
   *  up the new distance and regenerates pathData / primitive /
   *  cornerRounding accordingly — no need to recompute geometry
   *  here. Preserves every other field on `offsetSource`. */
  private commitOffsetDistanceEdit(constructionId: string, distance: number) {
    this.host.commit("Edit offset distance", (proj) => ({
      ...proj,
      constructionLines: (proj.constructionLines ?? []).map((l) => {
        if (l.id !== constructionId || !l.offsetSource) return l;
        return {
          ...l,
          offsetSource: { ...l.offsetSource, distance },
        };
      }),
    }));
    this.host.setSelection({
      pieceId: null,
      decorationId: null,
      constructionId,
    });
  }

  // -------------------------------------------------------------------
  // Preview
  // -------------------------------------------------------------------

  /** Recompute the offset preview from the source line's path and
   *  the cursor's current perpendicular signed distance. The
   *  preview is a yellow dashed outline that tracks the cursor.
   *  Sign of the distance follows the side the cursor is on: for
   *  closed shapes, "outside" = positive (expand outward); for
   *  open paths, the path normal at the nearest point picks the
   *  sign. */
  private refreshOffsetPreview(cursor: { x: number; y: number }) {
    if (!this.offsetDrag) return;
    const scope = this.host.getScope();
    const source = projectConstructionLines(this.host.getProject()).find(
      (l) => l.id === this.offsetDrag!.sourceId,
    );
    if (!source) {
      this.cancel();
      return;
    }
    if (this.offsetDrag.previewItem) {
      this.offsetDrag.previewItem.remove();
      this.offsetDrag.previewItem = null;
    }
    let parsed: paper.Path | paper.CompoundPath | null = null;
    try {
      const cp = new scope.CompoundPath({
        pathData: source.pathData,
        insert: false,
      });
      if (cp.children && cp.children.length === 1) {
        const single = cp.children[0] as paper.Path;
        single.remove();
        cp.remove();
        parsed = single;
      } else {
        parsed = cp;
      }
    } catch {
      this.cancel();
      return;
    }
    const cursorPt = new scope.Point(cursor.x, cursor.y);
    // Single-segment offset (issue #681): measure distance/side against
    // just the picked edge, and always treat it as an open path (the
    // left-of-direction normal picks the side) so the preview matches
    // what `computeSingleSegmentOffsetPath` will emit.
    const segment = this.offsetDrag.segment ?? null;
    let loc: paper.CurveLocation | null;
    let closed: boolean;
    if (segment) {
      const subs: paper.Path[] =
        parsed instanceof scope.Path
          ? [parsed]
          : (parsed.children as paper.Path[]);
      const curve = subs[segment.pathIndex]?.curves?.[segment.curveIndex];
      loc = curve ? curve.getNearestLocation(cursorPt) : null;
      closed = false;
    } else {
      loc = parsed.getNearestLocation(cursorPt);
      // For a closed source, "inside" flips the sign so a drag toward
      // the centre reads as a negative (inset) offset. For an open
      // path we use the local normal: dot product with (cursor - loc)
      // picks the side.
      closed =
        parsed instanceof scope.Path
          ? parsed.closed
          : parsed.children.every((c) => (c as paper.Path).closed);
    }
    if (!loc) {
      parsed.remove();
      return;
    }
    const magnitude = loc.point.getDistance(cursorPt);
    let signedDistance = magnitude;
    if (closed) {
      if (parsed.contains(cursorPt)) signedDistance = -magnitude;
    } else {
      // PaperOffset's sign convention for open paths is "positive =
      // LEFT of the path's traversal direction" in y-down screen
      // space, i.e., the CCW-90° normal of the tangent. The cursor
      // ends up on the SAME side as the resulting offset, so the
      // signed distance has to match that convention — issue #166.
      // For a tangent (a, b), the left-of-direction normal in y-down
      // is (b, -a). Cursor on the same side as this normal → positive.
      const tan = loc.tangent.normalize();
      const leftNormal = new scope.Point(tan.y, -tan.x);
      const delta = cursorPt.subtract(loc.point);
      if (delta.dot(leftNormal) < 0) signedDistance = -magnitude;
    }
    // When the user has typed a preset distance, the magnitude is
    // fixed at that value — the cursor only contributes the SIGN
    // (which side of the line the offset goes to). Without a preset
    // we use the raw signed magnitude, and mirror it back into the
    // properties-panel input so the user can read it live.
    const preset = this.presetOffsetDistance;
    if (preset !== null && isFinite(preset) && preset !== 0) {
      const sign = signedDistance < 0 ? -1 : 1;
      signedDistance = preset * sign;
    } else {
      this.liveOffsetDistanceCallback?.(signedDistance);
    }
    this.offsetDrag.distance = signedDistance;
    // #746: ray from the source edge (the perpendicular foot) out to the offset
    // distance, labelled with the distance value.
    if (magnitude > 1e-6) {
      const ux = (cursorPt.x - loc.point.x) / magnitude;
      const uy = (cursorPt.y - loc.point.y) / magnitude;
      const d = Math.abs(signedDistance);
      this.host.getScene().setGuideRays([
        {
          from: { x: loc.point.x, y: loc.point.y },
          to: { x: loc.point.x + ux * d, y: loc.point.y + uy * d },
          lengthMm: d,
        },
      ]);
    }
    // Single-segment: one straight parallel line. Otherwise prefer the
    // heuristic for primitive sources so the preview matches what
    // `commitOffsetLine` will produce (issue #244).
    let previewPath: string | null;
    if (segment) {
      previewPath = computeSingleSegmentOffsetPath(
        source,
        signedDistance,
        segment,
        scope,
      );
    } else {
      const heuristic = computeHeuristicOffset(source, signedDistance, scope, {
        roundedCorners: this.roundedCorners,
      });
      previewPath = heuristic
        ? heuristic.pathData
        : computeOffsetPathData(source.pathData, signedDistance, scope);
    }
    parsed.remove();
    if (!previewPath) return;
    try {
      const preview = new scope.CompoundPath({
        pathData: previewPath,
        insert: true,
      });
      preview.strokeColor = new scope.Color(PREVIEW_STROKE());
      preview.strokeWidth = 1.6;
      preview.strokeScaling = false;
      preview.dashArray = [4, 3];
      preview.fillColor = null;
      preview.data = { ephemeral: true };
      this.offsetDrag.previewItem = preview;
    } catch {
      /* ignore — preview only */
    }
  }
}
